import { alpha, Theme } from '@mui/material/styles';

export type TaskPriorityKey = 'low' | 'medium' | 'high' | 'extreme' | 'ultra';

export type ProjectStatusKey = 'active' | 'completed' | 'archived' | 'on_hold';

/** Light card background by project status */
export function getProjectStatusCardSx(theme: Theme, status: ProjectStatusKey) {
  switch (status) {
    case 'active':
      return {
        bgcolor: alpha(theme.palette.success.main, 0.08),
        border: '1px solid',
        borderColor: alpha(theme.palette.success.main, 0.22)
      };
    case 'on_hold':
      return {
        bgcolor: alpha(theme.palette.warning.main, 0.1),
        border: '1px solid',
        borderColor: alpha(theme.palette.warning.main, 0.25)
      };
    case 'completed':
      return {
        bgcolor: alpha(theme.palette.info.main, 0.08),
        border: '1px solid',
        borderColor: alpha(theme.palette.info.main, 0.2)
      };
    case 'archived':
    default:
      return {
        bgcolor: alpha(theme.palette.grey[500], 0.1),
        border: '1px solid',
        borderColor: alpha(theme.palette.grey[500], 0.22)
      };
  }
}

/** Row / snippet background for tasks by priority (used when not overdue/started/near-due) */
export function getTaskPriorityRowSx(theme: Theme, priority?: string | null) {
  const p = (priority || 'medium') as TaskPriorityKey;
  switch (p) {
    case 'low':
      return {
        backgroundColor: alpha(theme.palette.info.main, 0.12),
        '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.2) }
      };
    case 'high':
      return {
        backgroundColor: alpha(theme.palette.warning.main, 0.12),
        '&:hover': { backgroundColor: alpha(theme.palette.warning.main, 0.22) }
      };
    case 'extreme':
      return {
        backgroundColor: alpha(theme.palette.warning.main, 0.22),
        '&:hover': { backgroundColor: alpha(theme.palette.warning.main, 0.32) }
      };
    case 'ultra':
      return {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.22) }
      };
    case 'medium':
    default:
      return {
        backgroundColor: alpha(theme.palette.grey[500], 0.08),
        '&:hover': { backgroundColor: alpha(theme.palette.grey[500], 0.14) }
      };
  }
}

/** Timeline bar fill + readable text on light tints */
export function getTaskPriorityBarSx(theme: Theme, priority?: string | null) {
  const p = (priority || 'medium') as TaskPriorityKey;
  switch (p) {
    case 'low':
      return {
        bgcolor: alpha(theme.palette.info.main, 0.35),
        color: theme.palette.info.dark
      };
    case 'high':
      return {
        bgcolor: alpha(theme.palette.warning.main, 0.35),
        color: theme.palette.warning.dark
      };
    case 'extreme':
      return {
        bgcolor: alpha(theme.palette.warning.main, 0.5),
        color: theme.palette.warning.dark
      };
    case 'ultra':
      return {
        bgcolor: alpha(theme.palette.error.main, 0.28),
        color: theme.palette.error.dark
      };
    case 'medium':
    default:
      return {
        bgcolor: alpha(theme.palette.primary.main, 0.22),
        color: theme.palette.primary.dark
      };
  }
}

function hashProjectId(projectId: string): number {
  let h = 0;
  const s = projectId || '';
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Stable light background per project (by id) — used for timeline task rows
 * and the project checkboxes above the timeline so colors match.
 */
export function projectTimelineRowBand(theme: Theme, projectId: string) {
  const bases = [
    theme.palette.primary.main,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.secondary?.main ?? theme.palette.primary.main,
    theme.palette.error.main,
    theme.palette.grey[600]
  ];
  const i = hashProjectId(projectId) % bases.length;
  return alpha(bases[i], theme.palette.mode === 'dark' ? 0.14 : 0.09);
}
